// src/features/ia/IASousFamilleView.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Page IA Sous-Famille — même logique/design que IAFamilleView, mais une ligne
// par SOUS-FAMILLE (toutes familles confondues), via /prevision-f/toutes-sous-familles
// (header gradient, controls card, table sticky, chips couleur, footer totaux,
// empty state)
// ─────────────────────────────────────────────────────────────────────────────
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { getForecastToutesSousFamilles, postPrevisionHorizon } from "../api";
import { useForecastDepots } from "../useForecastDepots";
import ComparisonView from "../../cube/components/ComparisonView";
import EvolutionView from "../../cube/components/EvolutionView";
import SaveQueryModal from "../../saved-queries/components/SaveQueryModal";
import SavedQueriesDropdown from "../../saved-queries/components/SavedQueriesDropdown";
import { thS } from "../../../shared/ui/styles";
import { Spinner } from "../../../shared/ui/Spinner";
import { colors } from "../../../shared/ui/tokens";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const TOP_OPTIONS = [5, 10, 20, 50];

const CHIP_PALETTE = [
  "#6B21A8",
  "#EC4899",
  "#0D9488",
  "#D97706",
  "#1D4ED8",
  "#EA580C",
  "#7C3AED",
  "#BE185D",
  "#047857",
  "#B45309",
  "#1E40AF",
  "#C2410C",
  "#6D28D9",
  "#9D174D",
  "#065F46",
  "#92400E",
];

const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.brand[100],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  rose: colors.accent[300],
  roseDark: colors.accent[500],
  roseMed: colors.accent[100],
  roseLight: colors.accent[50],
  white: colors.neutral[0],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  amber: colors.warning[500],
  amberLight: colors.warning[100],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
  success: colors.accent[500],
  successLight: colors.accent[50],
  teal: colors.accent[300],
  tealLight: colors.accent[100],
  orange: colors.warning[600],
  orangeLight: colors.warning[50],
  green: colors.accent[500],
  greenBg: colors.neutral[50],
  red: colors.danger[500],
  redBg: colors.neutral[100],
};const FONTS = {
  heading: "Poppins, Inter, system-ui, sans-serif",
  body: "Inter, system-ui, -apple-system, sans-serif",
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type ViewMode = "tableau" | "comparaison" | "evolution";

const VIEW_MODES: { mode: ViewMode; icon: string; label: string }[] = [
  { mode: "tableau", icon: "🔮", label: "Prévisions IA" },
  { mode: "comparaison", icon: "⚖️", label: "Comparaison" },
  { mode: "evolution", icon: "📈", label: "Évolution" },
];

type PrevisionData = Record<string, Record<string, number>>;

// NOUVEAU — sous-famille telle que renvoyée par /prevision-f/toutes-sous-familles
// (une ligne par paire réelle famille/sous-famille, toutes familles confondues)
interface RefSousFam {
  codFam: string; // code sous-famille
  desgFam: string; // libellé sous-famille
  codFamp: string; // code famille parente
  desgFamp: string; // libellé famille parente
}

// NOUVEAU — enveloppe générique des réponses de l'API Flask (jsonify)
// NOUVEAU — forme exacte de la réponse POST /prevision-f/prevision-horizon
// (voir route.py : jsonify({success, famille, sous_famille, depot, vendeur,
//  horizon, ca_total, date_from, date_to, previsions}))
interface PrevisionHorizonItem {
  step: number;
  date: string;
  ca_predit: number;
  borne_basse: number;
  borne_haute: number;
}

interface PrevisionHorizonResponse {
  success: boolean;
  famille?: string;
  sous_famille?: string;
  depot?: string;
  vendeur?: string;
  horizon?: number;
  ca_total?: number;
  date_from?: string;
  date_to?: string;
  previsions?: PrevisionHorizonItem[];
  error?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Style helpers (identiques à FamilleView)
// ─────────────────────────────────────────────────────────────────────────────

const tdS = (e?: React.CSSProperties): React.CSSProperties => ({
  border: `1px solid ${NW.gray200}`,
  padding: "8px 14px",
  whiteSpace: "nowrap",
  fontSize: 12,
  ...e,
});

const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: NW.white,
  borderRadius: 14,
  border: `1.5px solid ${NW.gray200}`,
  boxShadow: "0 2px 16px rgba(24,75,140,0.07)",
  ...extra,
});

// ─────────────────────────────────────────────────────────────────────────────
// Formatage montant
// ─────────────────────────────────────────────────────────────────────────────

const fmtCa = (v: number | undefined | null): string => {
  if (v == null || isNaN(v as number)) return "—";
  return (
    new Intl.NumberFormat("fr-TN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v) + " DT"
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Spinner
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// IASousFamilleView — COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

const IASousFamilleView: React.FC = () => {
  // ── Données de référence ───────────────────────────────────────────────────
  // NOUVEAU — ici "sousFamilles" contient directement TOUTES les paires réelles
  // (famille, sous_famille), toutes familles confondues. Une seule requête
  // suffit (pas besoin du cache famille → sous-familles de la vue Famille).
  const [sousFamilles, setSousFamilles] = useState<RefSousFam[]>([]);
  const [loadingFam, setLoadingFam] = useState(false);
  const [famillesError, setFamillesError] = useState<string | null>(null);
  const [famillesRetryToken, setFamillesRetryToken] = useState(0);
  const [horizon, setHorizon] = useState<number>(4);
  // Dépôts servis par le modèle (GET /prevision-f/depots) — rechargés avec « Réessayer »
  const {
    depots,
    loading: loadingDepots,
    error: depotsError,
  } = useForecastDepots(famillesRetryToken);
  const loadError = famillesError ?? depotsError;

  // NOUVEAU — chargement de la liste des sous-familles (toutes familles
  // confondues) au montage. Sans cet appel, `sousFamilles` reste vide pour
  // toujours → horizonReady=false → les boutons Horizon (1..11) et Calculer
  // restent grisés indéfiniment. Route backend : GET /prevision-f/toutes-sous-familles
  // (voir route.py → get_toutes_sous_familles(), renvoie
  // {success, data:[{codFam, desgFam, codFamp, desgFamp}]})
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingFam(true);
      setFamillesError(null);
      try {
        const r = await getForecastToutesSousFamilles<RefSousFam>();
        if (!cancelled) {
          if (r.success === false) {
            setFamillesError(r.error || "Erreur inconnue du serveur");
            setSousFamilles([]);
          } else {
            setSousFamilles(r.data || []);
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          "Erreur chargement /prevision-f/toutes-sous-familles :",
          err,
        );
        if (!cancelled) {
          setFamillesError(
            err instanceof Error ? err.message : "Erreur réseau inconnue",
          );
          setSousFamilles([]);
        }
      } finally {
        if (!cancelled) setLoadingFam(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [famillesRetryToken]);

  // ── Filtres ────────────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState("");
  const [topN, setTopN] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("tableau");

  // ── Données IA ─────────────────────────────────────────────────────────────
  const [previsionData, setPrevisionData] = useState<PrevisionData>({});
  const [previsionLoading, setPrevisionLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ done: 0, total: 0 });

  // ── Save modal ─────────────────────────────────────────────────────────────
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedRefreshToken, setSavedRefreshToken] = useState(0);
  const [hasRun, setHasRun] = useState(false);

  // ── Annulation ─────────────────────────────────────────────────────────────
  const abortRef = useRef<AbortController | null>(null);

  // ── Validation ────────────────────────────────────────────────────────────
  // NOUVEAU — la route LightGBM v3 (/prevision-horizon) ne prend pas de plage
  // de dates : elle calcule toujours à partir d'aujourd'hui (voir route.py,
  // start_date=datetime.now().date()). Seul l'horizon (1 à 11 semaines) est
  // nécessaire → le bouton Calculer se débloque dès que les sous-familles sont
  // chargées et qu'un horizon est sélectionné (horizon a une valeur par
  // défaut de 4, donc il est toujours "sélectionné").
  const horizonReady =
    sousFamilles.length > 0 && !loadingFam && depots.length > 0 && !loadingDepots;
  const canCalculer = horizonReady && !previsionLoading;

  // ── Helpers ───────────────────────────────────────────────────────────────
  // NOUVEAU — clé unique par ligne : "codFamp::codFam", car un même code de
  // sous-famille (codFam) peut théoriquement se répéter sous des familles
  // parentes différentes.
  const getCode = (item: RefSousFam) => `${item.codFamp}::${item.codFam}`;

  // ── Items filtrés ─────────────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    const q = searchText.toLowerCase();
    return sousFamilles.filter(
      (item) =>
        !q ||
        item.codFam.toLowerCase().includes(q) ||
        item.desgFam.toLowerCase().includes(q) ||
        item.codFamp.toLowerCase().includes(q) ||
        item.desgFamp.toLowerCase().includes(q),
    );
  }, [sousFamilles, searchText]);

  const displayItems = useMemo(() => {
    if (!topN) return filteredItems;
    return [...filteredItems]
      .sort((a, b) => {
        const totA = depots.reduce(
          (s, d) => s + (previsionData[getCode(a)]?.[d] ?? 0),
          0,
        );
        const totB = depots.reduce(
          (s, d) => s + (previsionData[getCode(b)]?.[d] ?? 0),
          0,
        );
        return totB - totA;
      })
      .slice(0, topN);
  }, [filteredItems, topN, previsionData, depots]);

  // ── Run IA : toutes sous-familles (toutes familles confondues) × tous dépôts ──
  // (LightGBM v3) — plus simple que la vue Famille : pas de préchargement en
  // deux étapes, /toutes-sous-familles nous donne déjà toutes les paires.
  const runAll = useCallback(async () => {
    if (sousFamilles.length === 0 || depots.length === 0) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setPrevisionLoading(true);
    setPrevisionData({});
    setHasRun(false);

    const total = sousFamilles.length * depots.length;
    setLoadProgress({ done: 0, total });

    const result: PrevisionData = {};
    let done = 0;

    for (const sf of sousFamilles) {
      if (ctrl.signal.aborted) break;
      const code = getCode(sf);
      result[code] = {};

      for (const depot of depots) {
        if (ctrl.signal.aborted) break;
        try {
          // NOUVEAU — appel direct à la vraie route LightGBM v3, qui EXIGE
          // famille + sous_famille + depot (contrairement à l'ancien postPrevisionF
          // qui n'envoyait pas sous_famille → réponse 400 avalée en silence → "rien").
          const res = await postPrevisionHorizon<PrevisionHorizonResponse>(
            {
              famille: sf.codFamp,
              sous_famille: sf.codFam,
              depot,
              horizon,
            },
          );
          if (res.success && typeof res.ca_total === "number") {
            result[code][depot] = (result[code][depot] ?? 0) + res.ca_total;
          }
        } catch {
          /* combinaison famille/sous_famille/dépôt sans modèle valide — on ignore */
        }
        done++;
        setLoadProgress({ done, total });
      }
      setPrevisionData((prev) => ({ ...prev, [code]: { ...result[code] } }));
    }

    if (!ctrl.signal.aborted) {
      setPrevisionData({ ...result });
      setHasRun(true);
    }
    setPrevisionLoading(false);
  }, [sousFamilles, depots, horizon]);

  // ── Auto-run dès que les sous-familles et les dépôts sont chargés ─────────
  // (plus de période à attendre : la route /prevision-horizon calcule
  // toujours à partir d'aujourd'hui côté backend)
  useEffect(() => {
    if (sousFamilles.length === 0 || depots.length === 0) return;
    runAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sousFamilles, depots]);

  // ── Totaux colonnes ────────────────────────────────────────────────────────
  const depotTotals = useMemo(() => {
    const t: Record<string, number> = {};
    depots.forEach((dep) => {
      t[dep] = displayItems.reduce(
        (s, item) => s + (previsionData[getCode(item)]?.[dep] ?? 0),
        0,
      );
    });
    t["TOTAL"] = depots.reduce((s, d) => s + t[d], 0);
    return t;
  }, [displayItems, previsionData, depots]);

  // ── Grand total (pour header badge) ──────────────────────────────────────
  const grandTotal = depotTotals["TOTAL"] ?? 0;

  // ── Dépôts actifs ─────────────────────────────────────────────────────────
  const activeDepots = useMemo(() => {
    if (!hasRun && !previsionLoading) return depots;
    return depots.filter((dep) =>
      Object.values(previsionData).some((row) => (row[dep] ?? 0) > 0),
    );
  }, [previsionData, hasRun, previsionLoading, depots]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDU
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        fontFamily: FONTS.body,
        padding: "18px 20px",
        maxWidth: 1500,
        background: NW.gray50,
        minHeight: "100vh",
      }}
    >
      {/* ── CSS global ──────────────────────────────────────────────────── */}
      <style>{`
        @keyframes ia-spin    { to { transform: rotate(360deg); } }
        @keyframes ia-fadein  { from { opacity:0; transform:translateY(12px) scale(0.99); } to { opacity:1; transform:translateY(0) scale(1); } }
        .ia-row-hover:hover td { background: ${NW.violetSoft} !important; }
        .ia-view-btn { transition: all 0.15s; }
        .ia-view-btn:hover { filter: brightness(1.08); }
        ::-webkit-scrollbar { width:6px; height:6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${NW.violetMed}; border-radius: 99px; }
      `}</style>

      {/* ══ PAGE HEADER ═════════════════════════════════════════════════════ */}
      <div
        style={{
          background: `linear-gradient(135deg,${NW.violetDark} 0%,${NW.violet} 55%,#1D5BA8 100%)`,
          borderRadius: 16,
          padding: "18px 24px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          boxShadow: "0 6px 32px rgba(24,75,140,0.28)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* ligne rose décorative */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg,transparent,${NW.roseMed}80,${NW.roseMed}CC,${NW.roseMed}80,transparent)`,
            pointerEvents: "none",
          }}
        />

        {/* Icône */}
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
            flexShrink: 0,
            boxShadow: "0 0 0 1px rgba(255,255,255,0.15)",
          }}
        >
          🔮
        </div>

        {/* Titre */}
        <div>
          <div
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 800,
              fontSize: 20,
              color: "#FFFFFF",
              letterSpacing: -0.4,
            }}
          >
            Prévisions IA — Sous-Familles
          </div>
          <div
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.5)",
              fontWeight: 600,
              letterSpacing: 2,
              textTransform: "uppercase",
              marginTop: 2,
            }}
          >
            Modèle LightGBM v3 · Mesure Pttc Vente · Tous Dépôts
          </div>
        </div>

        {/* Badges stats (visibles après run) */}
        {(hasRun || previsionLoading) && (
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {/* Nb familles */}
            <div
              style={{
                textAlign: "center",
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 12,
                padding: "8px 18px",
              }}
            >
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#FFFFFF",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {displayItems.length}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                }}
              >
                Sous-Familles
              </div>
            </div>

            {/* Dépôts actifs */}
            {hasRun && (
              <div
                style={{
                  textAlign: "center",
                  background: "rgba(125,168,217,0.18)",
                  border: "1px solid rgba(125,168,217,0.35)",
                  borderRadius: 12,
                  padding: "8px 18px",
                }}
              >
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: NW.roseMed,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {activeDepots.length}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "rgba(255,255,255,0.55)",
                    textTransform: "uppercase",
                    letterSpacing: 1.2,
                  }}
                >
                  Dépôts actifs
                </div>
              </div>
            )}

            {/* Total général */}
            {hasRun && grandTotal > 0 && (
              <div
                style={{
                  textAlign: "center",
                  background: "rgba(0,160,140,0.18)",
                  border: "1px solid rgba(0,160,140,0.4)",
                  borderRadius: 12,
                  padding: "8px 18px",
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: "#7FC8BD",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {fmtCa(grandTotal)}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "rgba(255,255,255,0.55)",
                    textTransform: "uppercase",
                    letterSpacing: 1.2,
                  }}
                >
                  Total Prévu
                </div>
              </div>
            )}

            {/* Progression */}
            {previsionLoading && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 12,
                  padding: "8px 16px",
                }}
              >
                <Spinner size={12} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#FFFFFF" }}>
                  {loadProgress.done} / {loadProgress.total}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ══ CONTROLS CARD ═══════════════════════════════════════════════════ */}
      <div style={card({ padding: "16px 20px", marginBottom: 14 })}>
        {/* ── Mesure (badge fixe Pttc) ── */}
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: NW.violet,
              textTransform: "uppercase",
              letterSpacing: 1.5,
              marginBottom: 7,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>🛒</span> Mesure active
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 14px",
                borderRadius: 9,
                background: NW.violetLight,
                border: `1.5px solid ${NW.violet}`,
                color: NW.violetDark,
                fontWeight: 700,
                fontSize: 13,
                boxShadow: `0 0 0 2px ${NW.violet}22`,
              }}
            >
              <input
                type="checkbox"
                checked
                readOnly
                style={{ accentColor: NW.violet, margin: 0 }}
              />
              Pttc Vente
            </span>
          </div>
        </div>

        {/* ── Filtres + boutons ── */}
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Top N — déplacé en premier */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 700,
                fontSize: 12,
                color: NW.gray800,
              }}
            >
              🏆 Top N
            </span>
            {TOP_OPTIONS.map((n) => (
              <button
                key={n}
                onClick={() => setTopN(topN === n ? null : n)}
                style={{
                  fontSize: 11,
                  padding: "3px 10px",
                  borderRadius: 99,
                  cursor: "pointer",
                  border: `1.5px solid ${topN === n ? NW.violet : NW.gray200}`,
                  background: topN === n ? NW.violetLight : NW.white,
                  color: topN === n ? NW.violet : NW.gray500,
                  fontWeight: topN === n ? 700 : 500,
                  transition: "all 0.15s",
                }}
              >
                {n}
              </button>
            ))}
            {topN !== null && (
              <button
                onClick={() => setTopN(null)}
                style={{
                  fontSize: 11,
                  padding: "3px 10px",
                  borderRadius: 99,
                  cursor: "pointer",
                  border: `1.5px solid ${NW.gray200}`,
                  background: "transparent",
                  color: NW.gray500,
                }}
              >
                Tous
              </button>
            )}
          </div>

          {/* Horizon (semaines) — NOUVEAU : désactivé tant que la période n'est pas valide
      et que les familles ne sont pas chargées (horizonReady) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 700,
                fontSize: 12,
                color: NW.gray800,
              }}
            >
              📅 HORIZON (SEMAINES)
            </span>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n) => (
              <button
                key={n}
                disabled={!horizonReady}
                onClick={() => setHorizon(n)}
                style={{
                  fontSize: 12,
                  padding: "5px 12px",
                  borderRadius: 10,
                  cursor: horizonReady ? "pointer" : "not-allowed",
                  border: `1.5px solid ${horizon === n && horizonReady ? NW.violet : NW.gray200}`,
                  background: !horizonReady
                    ? NW.gray100
                    : horizon === n
                      ? NW.violet
                      : NW.white,
                  color: !horizonReady
                    ? NW.gray300
                    : horizon === n
                      ? "#FFFFFF"
                      : NW.gray500,
                  fontWeight: horizon === n ? 700 : 500,
                  opacity: horizonReady ? 1 : 0.6,
                  transition: "all 0.15s",
                }}
              >
                {n}
              </button>
            ))}
            <span style={{ fontSize: 11, color: NW.gray500, marginLeft: 4 }}>
              {horizonReady ? (
                <>
                  Horizon sélectionné :{" "}
                  <strong style={{ color: NW.violet }}>
                    {horizon} semaine{horizon > 1 ? "s" : ""}
                  </strong>
                </>
              ) : loadError ? (
                <span
                  style={{
                    color: NW.red,
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  ⚠ {loadError}
                  <button
                    onClick={() => setFamillesRetryToken((t) => t + 1)}
                    style={{
                      fontSize: 11,
                      padding: "2px 10px",
                      borderRadius: 99,
                      cursor: "pointer",
                      border: `1.5px solid ${NW.red}`,
                      background: NW.redBg,
                      color: NW.red,
                      fontWeight: 700,
                    }}
                  >
                    ↻ Réessayer
                  </button>
                </span>
              ) : loadingFam ? (
                "← chargement des sous-familles en cours…"
              ) : (
                "← aucune sous-famille disponible"
              )}
            </span>
          </div>
          {/* Bouton Recalculer / Arrêter — NOUVEAU : désactivé tant que canCalculer=false */}
          {previsionLoading ? (
            <button
              onClick={() => {
                abortRef.current?.abort();
                setPrevisionLoading(false);
              }}
              style={{
                marginLeft: "auto",
                padding: "9px 18px",
                fontSize: 12,
                fontWeight: 700,
                border: `1.5px solid ${NW.red}`,
                borderRadius: 10,
                background: NW.redBg,
                color: NW.red,
                cursor: "pointer",
                fontFamily: FONTS.body,
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              ⏹ Arrêter
            </button>
          ) : (
            <button
              onClick={() => canCalculer && runAll()}
              disabled={!canCalculer}
              style={{
                marginLeft: "auto",
                padding: "9px 22px",
                background: !canCalculer
                  ? NW.gray200
                  : NW.violet,
                color: !canCalculer ? NW.gray500 : "#FFFFFF",
                border: "none",
                borderRadius: 10,
                cursor: !canCalculer ? "not-allowed" : "pointer",
                fontWeight: 700,
                fontSize: 13,
                fontFamily: FONTS.body,
                boxShadow: !canCalculer
                  ? "none"
                  : `0 4px 16px rgba(24,75,140,0.28)`,
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              {loadingFam ? (
                <>
                  <Spinner size={14} /> Chargement…
                </>
              ) : (
                <>⚡ Calculer</>
              )}
            </button>
          )}
        </div>

        {/* Barre de progression */}
        {previsionLoading && loadProgress.total > 0 && (
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                color: NW.gray500,
                marginBottom: 4,
              }}
            >
              <span>
                Calcul en cours… {loadProgress.done} / {loadProgress.total}{" "}
                appels
              </span>
              <span>
                {Math.round((loadProgress.done / loadProgress.total) * 100)}%
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: NW.violetLight,
                borderRadius: 99,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.round((loadProgress.done / loadProgress.total) * 100)}%`,
                  background: NW.violet,
                  borderRadius: 99,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ══ ONGLETS VUE + SAVE ══════════════════════════════════════════════ */}
      {(hasRun || previsionLoading) && (
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: NW.gray500,
              fontWeight: 600,
              marginRight: 4,
            }}
          >
            Vue :
          </span>
          {VIEW_MODES.map(({ mode, icon, label }) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className="ia-view-btn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 13px",
                borderRadius: 8,
                cursor: "pointer",
                border: `1.5px solid ${viewMode === mode ? NW.violet : NW.gray200}`,
                background:
                  viewMode === mode
                    ? NW.violetLight
                    : NW.white,
                color: viewMode === mode ? NW.violet : NW.gray500,
                fontWeight: viewMode === mode ? 700 : 500,
                fontSize: 12,
                fontFamily: FONTS.body,
                boxShadow:
                  viewMode === mode
                    ? `0 2px 8px rgba(24,75,140,0.12)`
                    : undefined,
              }}
            >
              <span style={{ fontSize: 13 }}>{icon}</span>
              {label}
            </button>
          ))}

          {displayItems.length > 0 && viewMode === "tableau" && (
            <span
              style={{
                fontSize: 11,
                color: NW.gray500,
                background: NW.gray100,
                borderRadius: 99,
                padding: "3px 10px",
                fontWeight: 600,
              }}
            >
              {displayItems.length} famille
              {displayItems.length !== 1 ? "s" : ""}
            </span>
          )}

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <button
              onClick={() => setShowSaveModal(true)}
              disabled={!hasRun}
              style={{
                background: hasRun
                  ? NW.violet
                  : NW.gray200,
                border: "none",
                borderRadius: 9,
                padding: "7px 16px",
                color: hasRun ? "#FFFFFF" : NW.gray500,
                fontWeight: 700,
                fontSize: 12,
                cursor: hasRun ? "pointer" : "not-allowed",
                fontFamily: FONTS.body,
                boxShadow: hasRun ? `0 3px 10px rgba(24,75,140,0.22)` : "none",
                display: "flex",
                alignItems: "center",
                gap: 6,
                opacity: hasRun ? 1 : 0.52,
                whiteSpace: "nowrap",
              }}
            >
              💾 Enregistrer
            </button>
            <SavedQueriesDropdown
              onSelect={() => {}}
              refreshToken={savedRefreshToken}
              currentType="ia-sous-famille"
            />
          </div>
        </div>
      )}

      {/* ══ VUES COMPARAISON / ÉVOLUTION ══════════════════════════════════ */}
      {viewMode === "comparaison" && (
        <div
          style={{
            ...card(),
            padding: 20,
            animation: "ia-fadein 0.38s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <ComparisonView initialComparisonPeriods={null} autoRunToken={0} />
        </div>
      )}
      {viewMode === "evolution" && (
        <div
          style={{
            ...card(),
            padding: 20,
            animation: "ia-fadein 0.38s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <EvolutionView />
        </div>
      )}

      {/* ══ VUE TABLEAU ═══════════════════════════════════════════════════ */}
      {viewMode === "tableau" && (hasRun || previsionLoading) && (
        <div style={card()}>
          {/* Filter bar */}
          <div
            style={{
              padding: "12px 16px",
              background: `linear-gradient(90deg,${NW.violetSoft} 0%,${NW.white} 100%)`,
              borderBottom: `1.5px solid ${NW.violetMed}`,
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Recherche */}
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 13,
                  color: NW.violet,
                }}
              >
                🔍
              </span>
              <input
                type="text"
                placeholder="Rechercher une famille…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  border: `1.5px solid ${searchText ? NW.violet : NW.gray200}`,
                  borderRadius: 10,
                  padding: "7px 10px 7px 32px",
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  color: NW.gray800,
                  outline: "none",
                  background: NW.white,
                  width: 220,
                  boxShadow: searchText
                    ? `0 0 0 3px ${NW.violetMed}`
                    : undefined,
                  transition: "all 0.15s",
                }}
              />
              {searchText && (
                <button
                  onClick={() => setSearchText("")}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: NW.gray500,
                    fontSize: 14,
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {/* Badge filtré */}
            {searchText && (
              <span
                style={{
                  padding: "4px 12px",
                  background: NW.violetLight,
                  border: `1.5px solid ${NW.violet}44`,
                  borderRadius: 99,
                  fontSize: 11,
                  color: NW.violetDark,
                  fontWeight: 700,
                }}
              >
                {displayItems.length} résultat
                {displayItems.length !== 1 ? "s" : ""}
              </span>
            )}

            {/* Compteur dépôts actifs */}
            {hasRun && (
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 11,
                  color: NW.gray500,
                  background: NW.gray100,
                  borderRadius: 99,
                  padding: "3px 10px",
                  fontWeight: 600,
                }}
              >
                {activeDepots.length} dépôt
                {activeDepots.length !== 1 ? "s" : ""} actif
                {activeDepots.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Tableau */}
          <div
            style={{
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: 560,
              animation: "ia-fadein 0.38s cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            <table
              style={{
                borderCollapse: "separate",
                borderSpacing: 0,
                width: "100%",
                fontSize: 12,
                fontFamily: FONTS.body,
              }}
            >
              {/* ── EN-TÊTE ────────────────────────────────────────────── */}
              <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                <tr
                  style={{
                    background: `linear-gradient(90deg,${NW.violetDark} 0%,${NW.violet} 100%)`,
                  }}
                >
                  <th
                    style={thS({
                      textAlign: "left",
                      color: "#FFFFFF",
                      position: "sticky",
                      left: 0,
                      background: NW.violetDark,
                      zIndex: 11,
                      minWidth: 180,
                      fontFamily: FONTS.heading,
                      borderRight: `2px solid ${NW.rose}55`,
                    })}
                  >
                    🗂️ Sous-Famille
                  </th>
                  {depots.map((dep) => (
                    <th
                      key={dep}
                      style={thS({
                        color: NW.roseMed,
                        fontFamily: FONTS.heading,
                        minWidth: 110,
                        opacity: (depotTotals[dep] ?? 0) > 0 ? 1 : 0.4,
                      })}
                    >
                      {dep}
                    </th>
                  ))}
                  <th
                    style={thS({
                      color: "#FCE7BF",
                      fontFamily: FONTS.heading,
                      minWidth: 140,
                      background: "rgba(255,255,255,0.10)",
                      borderLeft: `3px solid rgba(255,255,255,0.25)`,
                    })}
                  >
                    ∑ Total
                  </th>
                </tr>
              </thead>

              {/* ── CORPS ─────────────────────────────────────────────── */}
              <tbody>
                {loadingFam ? (
                  <tr>
                    <td
                      colSpan={depots.length + 2}
                      style={{
                        padding: 52,
                        textAlign: "center",
                        color: NW.gray500,
                        fontSize: 13,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <Spinner size={20} />
                        <span style={{ fontWeight: 600 }}>
                          Chargement des sous-familles…
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : displayItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={depots.length + 2}
                      style={{
                        padding: 52,
                        textAlign: "center",
                        color: NW.gray500,
                        fontSize: 13,
                      }}
                    >
                      {searchText
                        ? `Aucun résultat pour « ${searchText} »`
                        : "Aucune sous-famille disponible"}
                    </td>
                  </tr>
                ) : (
                  displayItems.map((item, ri) => {
                    const code = getCode(item);
                    const depData = previsionData[code] ?? {};
                    const rowTotal = depots.reduce(
                      (s, d) => s + (depData[d] ?? 0),
                      0,
                    );
                    const isLoading =
                      previsionLoading && Object.keys(depData).length === 0;
                    const chipColor = CHIP_PALETTE[ri % CHIP_PALETTE.length];

                    return (
                      <tr
                        key={code}
                        className="ia-row-hover"
                        style={{
                          background: ri % 2 === 0 ? NW.white : NW.gray50,
                          cursor: "default",
                        }}
                      >
                        {/* Sous-Famille (+ famille parente en sous-titre) */}
                        <td
                          style={tdS({
                            fontWeight: 700,
                            color: NW.gray800,
                            position: "sticky",
                            left: 0,
                            background: ri % 2 === 0 ? NW.white : NW.gray50,
                            zIndex: 5,
                            borderRight: `2px solid ${NW.gray200}`,
                            maxWidth: 220,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "normal",
                          })}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: chipColor,
                                flexShrink: 0,
                              }}
                            />
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                lineHeight: 1.3,
                              }}
                            >
                              {isLoading ? (
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <Spinner size={11} />
                                  {item.codFam}
                                </span>
                              ) : (
                                <span>{item.codFam}</span>
                              )}
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 500,
                                  color: NW.gray500,
                                }}
                              >
                                {item.codFamp}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Dépôts */}
                        {depots.map((dep) => {
                          const val = depData[dep] ?? null;
                          return (
                            <td
                              key={dep}
                              style={tdS({
                                textAlign: "right",
                                fontVariantNumeric: "tabular-nums",
                              })}
                            >
                              {isLoading ? (
                                <span style={{ opacity: 0.4 }}>
                                  <Spinner size={9} />
                                </span>
                              ) : val != null && val > 0 ? (
                                <span
                                  style={{ fontWeight: 700, color: NW.gray800 }}
                                >
                                  {fmtCa(val)}
                                </span>
                              ) : (
                                <span style={{ color: NW.gray300 }}>—</span>
                              )}
                            </td>
                          );
                        })}

                        {/* Total ligne */}
                        <td
                          style={tdS({
                            textAlign: "right",
                            fontWeight: 800,
                            fontVariantNumeric: "tabular-nums",
                            borderLeft: `3px solid ${NW.rose}55`,
                            background:
                              rowTotal > 0
                                ? ri % 2 === 0
                                  ? NW.violetSoft
                                  : "#EEF4FB"
                                : "transparent",
                            color: rowTotal > 0 ? NW.violetDark : NW.gray500,
                          })}
                        >
                          {rowTotal > 0 ? fmtCa(rowTotal) : "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {/* ── FOOTER TOTAUX ────────────────────────────────────── */}
              {hasRun && displayItems.length > 1 && (
                <tfoot style={{ position: "sticky", bottom: 0, zIndex: 5 }}>
                  <tr>
                    <td
                      style={tdS({
                        background: NW.violetDark,
                        color: "#FFFFFF",
                        fontWeight: 800,
                        fontSize: 12,
                        fontFamily: FONTS.heading,
                        borderRight: `3px solid ${NW.rose}`,
                        position: "sticky",
                        left: 0,
                        zIndex: 10,
                      })}
                    >
                      ∑ TOTAL GÉNÉRAL
                    </td>
                    {depots.map((dep) => (
                      <td
                        key={dep}
                        style={tdS({
                          background: NW.violetDark,
                          color: NW.roseMed,
                          fontWeight: 700,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        })}
                      >
                        {(depotTotals[dep] ?? 0) > 0
                          ? fmtCa(depotTotals[dep])
                          : "—"}
                      </td>
                    ))}
                    <td
                      style={tdS({
                        background: NW.violetDark,
                        color: "#FCE7BF",
                        fontWeight: 900,
                        fontSize: 13,
                        textAlign: "right",
                        borderLeft: `3px solid ${NW.rose}`,
                        fontVariantNumeric: "tabular-nums",
                      })}
                    >
                      {fmtCa(grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Légende */}
          <div
            style={{
              padding: "10px 16px",
              borderTop: `1px solid ${NW.gray100}`,
              display: "flex",
              gap: 20,
              fontSize: 11,
              color: NW.gray500,
              flexWrap: "wrap",
            }}
          >
            <span>🏭 Colonnes = dépôts ({depots.join(", ")})</span>
          </div>
        </div>
      )}

      {/* ══ EMPTY STATE ═══════════════════════════════════════════════════ */}
      {viewMode === "tableau" && !hasRun && !previsionLoading && (
        <div
          style={{
            color: NW.gray500,
            padding: 60,
            textAlign: "center",
            fontSize: 14,
            background: NW.white,
            borderRadius: 16,
            border: `1.5px dashed ${NW.violetMed}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 48 }}>🔮</span>
          <div
            style={{
              fontWeight: 800,
              color: NW.violet,
              fontSize: 16,
              fontFamily: FONTS.heading,
            }}
          >
            Aucune prévision affichée
          </div>
          <div style={{ fontSize: 13, color: NW.gray500, lineHeight: 1.7 }}>
            Sélectionnez une période valide puis cliquez{" "}
            <strong style={{ color: NW.violet }}>⚡ Calculer</strong>
            <br />
          </div>
        </div>
      )}

      {/* ══ SAVE MODAL ══════════════════════════════════════════════════════ */}
      {showSaveModal && (
        <SaveQueryModal
          onClose={() => setShowSaveModal(false)}
          onSaved={() => setSavedRefreshToken((t) => t + 1)}
          type="ia-sous-famille"
          section={viewMode}
          overrideSoldeCodePeriode={null}
          overrideDemarquePhase={null}
          overrideDateFrom={null}
          overrideDateTo={null}
          overrideTopN={topN}
        />
      )}
    </div>
  );
};

export default IASousFamilleView;